<template>
    <div ref="popup" class="ol-popup feature-popup" v-show="hasFeatures">
        <a href="#" class="ol-popup-closer" @click.prevent="close"></a>

        <div class="popup-content">
            <div v-if="currentFeature">
                <h6 class="layer-title">{{ getFeatureLayerName(currentFeature) }}</h6>

                <div class="attributes-table-container">
                    <table class="table table-striped table-sm attributes-table">
                        <tbody>
                            <tr v-for="(value, key) in displayProperties" :key="key">
                                <td class="attr-key">{{ key }}</td>
                                <td class="attr-value">{{ value }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else class="no-data">
                <p>No se encontraron resultados.</p>
            </div>
        </div>

        <div class="popup-nav" v-if="features.length > 0">
            <button class="btn-nav" :disabled="currentIndex === 0" @click="prevFeature" v-if="features.length > 1">
                <font-awesome-icon icon="chevron-left" />
            </button>

            <span class="count-span">
                {{ currentIndex + 1 }} / {{ features.length }}
            </span>

            <button class="btn-nav" :disabled="currentIndex === features.length - 1" @click="nextFeature"
                v-if="features.length > 1">
                <font-awesome-icon icon="chevron-right" />
            </button>
        </div>
    </div>
</template>

<script>
import Overlay from 'ol/Overlay';
import { selectService } from '@/services/map/selectService';
import { mapService } from '@/services/map/mapService';

export default {
    name: 'FeaturePopup',
    data() {
        return {
            overlay: null,
            currentIndex: 0
        }
    },
    computed: {
        // Reactive state from the service
        features() {
            return selectService.state.features;
        },
        location() {
            return selectService.state.location;
        },
        hasFeatures() {
            return this.features && this.features.length > 0;
        },
        currentFeature() {
            if (!this.hasFeatures) return null;
            return this.features[this.currentIndex];
        },
        displayProperties() {
            return this.getFeatureProperties(this.currentFeature);
        }
    },
    watch: {
        location(newLoc) {
            if (this.overlay) {
                this.overlay.setPosition(newLoc);
                // Reset index when location changes (new selection)
                if (newLoc) {
                    this.currentIndex = 0;
                }
            }
        },
        features() {
            this.currentIndex = 0;
        }
    },
    mounted() {
        this.initOverlay();
    },
    beforeDestroy() {
        if (this.overlay && mapService.get()) {
            mapService.get().removeOverlay(this.overlay);
        }
    },
    methods: {
        getFeatureLayerName(feature) {
            if (!feature) return 'Capa Desconocida';
            // Handle OpenLayers Feature
            if (typeof feature.get === 'function') {
                return feature.get('layerName') || 'Capa Desconocida';
            }
            // Handle POJO
            return feature.layerName || 'Capa Desconocida';
        },
        getFeatureProperties(feature) {
            if (!feature) return {};
            let props = {};
            
            // Handle OpenLayers Feature
            if (typeof feature.getProperties === 'function') {
                props = feature.getProperties();
            } else {
                // Handle POJO
                props = feature.properties || feature.attributes || {};
            }

            const filtered = {};
            // Filter out internal or complex objects
            for (const [key, value] of Object.entries(props)) {
                // Filter out 'geometry' explicitly if present in properties
                if (key !== 'bbox' && key !== 'geometry' && typeof value !== 'object' && typeof value !== 'function') {
                    filtered[key] = value;
                }
            }
            return filtered;
        },
        initOverlay() {
            const map = mapService.get();
            if (!map) return;

            this.overlay = new Overlay({
                element: this.$el,
                autoPan: true,
                autoPanAnimation: {
                    duration: 250,
                },
            });

            map.addOverlay(this.overlay);
        },
        close() {
            selectService.clearResults();
        },
        nextFeature() {
            if (this.currentIndex < this.features.length - 1) {
                this.currentIndex++;
            }
        },
        prevFeature() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
            }
        }
    }
}
</script>

<style scoped lang="scss">
.feature-popup {
    background-color: white;
    filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    bottom: 12px;
    left: -50px;
    min-width: 300px;
    font-family: 'Inter', sans-serif;
    color: #334155;
    display: flex;
    flex-direction: column;
    gap: 10px;

    // Arrow styling (pseudo-elements)
    &:after,
    &:before {
        top: 100%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
    }

    &:after {
        border-top-color: white;
        border-width: 10px;
        left: 48px;
        margin-left: -10px;
    }

    &:before {
        border-top-color: #e2e8f0;
        border-width: 11px;
        left: 48px;
        margin-left: -11px;
    }
}

.ol-popup-closer {
    text-decoration: none;
    position: absolute;
    top: 8px;
    right: 12px;
    color: #64748b;
    font-weight: bold;
    font-size: 1.1rem;

    &:after {
        content: "✖";
    }

    &:hover {
        color: #ef4444;
    }
}

.layer-title {
    margin: 0 0 10px 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 2px solid #3b82f6;
    padding-bottom: 5px;
    display: inline-block;
}

.popup-content {
    max-height: 250px;
    overflow-y: auto;
}

.attributes-table-container {
    background: #f8fafc;
    border-radius: 4px;
    overflow: hidden;
}

.attributes-table {
    margin-bottom: 0;
    font-size: 0.85rem;
    width: 100%;

    td {
        padding: 5px 8px;
        border-bottom: 1px solid #e2e8f0;
    }

    .attr-key {
        font-weight: 600;
        color: #475569;
        width: 40%;
        background-color: #f1f5f9;
    }

    .attr-value {
        color: #1e293b;
    }
}

.popup-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 5px;
    border-top: 1px solid #e2e8f0;
    padding-top: 10px;
}

.count-span {
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
}

.btn-nav {
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background 0.2s;

    &:hover:not(:disabled) {
        background-color: #2563eb;
    }

    &:disabled {
        background-color: #cbd5e1;
        cursor: not-allowed;
    }
}

.no-data {
    color: #64748b;
    font-style: italic;
    text-align: center;
    padding: 10px;
}
</style>
